import React, { useEffect, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Center,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack
} from '@chakra-ui/react'

import { addDays, differenceInMonths, eachMonthOfInterval, format, isValid } from 'date-fns'
import type { CustomRenewalPreview, RenewalInternetFeeMode } from '@homevest/types/renewals'
import { agreementSigners, capabilities, regions } from '@homevest/utils'
import { calculateRenewalLeaseEndDate } from '@homevest/utils/dates'

import { useRentalForRenewalQuery } from 'graphql/generated'
import {
  getAddressForRental,
  getLlc,
  getRentalLeaseSigners,
  getSecurityDepositCreditAmount
} from 'lib/rentals'
import axios from 'lib/axios'
import { formatDate, parseDate } from 'lib/date-time'

import RenewRentalModalForm from './RenewRentalModalForm'
import RenewalBillingFields, { CreditEntry, validCreditAmount } from './RenewalBillingFields'
import RenewRentalModalDetails from './RenewRentalModalDetails'
import RenewRentalConfirmation from './RenewRentalConfirmation'
import {
  excludeHoldoverAgreementHistories,
  getLatestNonHoldoverAgreementHistory
} from './renewalAgreementHistories'
import {
  calculateRenewalTermFromDates,
  isValidCustomRenewalTerm,
  isValidRenewalOptionPremium,
  isValidRenewalRent,
  isValidRenewalTerm
} from './renewalFormValidation'
import { AgreementSigners } from 'types/FullRental'
import { RenewalDetails } from 'types/Renewals'
import { StoreState } from 'store'
import { hasCapability } from 'lib/admin-perms'

const { VANILLA_LEASE_PURCHASE_OPTION_SENDER } = capabilities.CAPABILITY_TYPES
const DEFAULT_KCPOA_STEP_UP_PERCENTAGE = 4
const DEFAULT_INITIAL_OPTION_FEE = 5000
const DEFAULT_MONTHLY_OPTION_FEE = 300

const RenewRentalModal: React.FC<
  React.PropsWithChildren<{
    rentalId: string
    isOpen: boolean
    onSuccess?: () => void
    setOpen: (closed: boolean) => void
  }>
> = ({ rentalId, isOpen, onSuccess, setOpen }) => {
  const [{ data }] = useRentalForRenewalQuery({
    pause: !isOpen,
    variables: { id: rentalId }
  })

  const rental = data?.rentals_by_pk
  const admin = useSelector((state: StoreState) => state.admin)
  const preserveFormOnNextOpen = useRef(false)
  const modalBodyRef = useRef<HTMLDivElement>(null)
  const internetModeInitialized = useRef(false)
  const [confirmationIsOpen, setConfirmationIsOpen] = useState(false)
  const [optionPremium, setOptionPremium] = useState(0)
  const [leaseVariant, setLeaseVariant] = useState<'standard' | 'kcpoa'>('standard')
  const [initialOptionFee, setInitialOptionFee] = useState(DEFAULT_INITIAL_OPTION_FEE)
  const [monthlyOptionFee, setMonthlyOptionFee] = useState(DEFAULT_MONTHLY_OPTION_FEE)
  const [depositCredit, setDepositCredit] = useState(0)
  const [purchasePrice, setPurchasePrice] = useState<number | null>(null)
  const [notes, setNotes] = useState('')
  const [term, setTerm] = useState(12)
  const [rent, setRent] = useState(0)
  const [showOtherTerm, setShowOtherTerm] = useState<boolean>(false)
  const [customLeaseEndDate, setCustomLeaseEndDate] = useState<Date | null>(null)
  const [newAgreementSigners, setAgreementSigners] = useState<AgreementSigners>()
  const [agreementTemplateName, setAgreementTemplateName] = useState('')
  const [templateError, setTemplateError] = useState('')
  const [isTemplateLoading, setIsTemplateLoading] = useState(false)
  const [step, setStep] = useState<'terms' | 'billing'>('terms')
  const [removedAddOnIds, setRemovedAddOnIds] = useState<string[]>([])
  const [internetFeeMode, setInternetFeeMode] = useState<RenewalInternetFeeMode>()
  const [credits, setCredits] = useState<CreditEntry[]>([])
  const [concessionDescription, setConcessionDescription] = useState('')
  const [concessionTotal, setConcessionTotal] = useState('')
  const [billingPreview, setBillingPreview] = useState<CustomRenewalPreview | null>(null)
  const [previewKey, setPreviewKey] = useState('')
  const [previewError, setPreviewError] = useState('')
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewRetry, setPreviewRetry] = useState(0)

  useEffect(() => {
    if (isOpen) modalBodyRef.current?.scrollTo({ top: 0 })
  }, [isOpen, step])

  const canUseKCPOA = hasCapability(admin, VANILLA_LEASE_PURCHASE_OPTION_SENDER)
  const isKCPOAEligible = Boolean(
    rental?.rental_program_type === 'vanilla' &&
      rental.portfolio_home?.home?.market?.name === regions.REGIONS.KANSAS_CITY &&
      canUseKCPOA
  )
  const isKCPOA = leaseVariant === 'kcpoa'

  useEffect(() => {
    if (isOpen && data?.rentals_by_pk) {
      if (preserveFormOnNextOpen.current) {
        preserveFormOnNextOpen.current = false
        return
      }

      const newRental = data.rentals_by_pk
      const renewalAgreementHistories = excludeHoldoverAgreementHistories(
        newRental.rental_agreement_histories
      )
      const latestAgreementHistory = getLatestNonHoldoverAgreementHistory(renewalAgreementHistories)
      const agreementSigners = getRentalLeaseSigners({
        ...newRental,
        rental_agreement_histories: renewalAgreementHistories
      })
      const previousRent = Number(latestAgreementHistory?.rent ?? 0)
      const currentOptionPremium = newRental.target_monthly_option_premium / 100
      setStep('terms')
      setRemovedAddOnIds([])
      setInternetFeeMode(undefined)
      internetModeInitialized.current = false
      setCredits([])
      setConcessionDescription('')
      setConcessionTotal('')
      setBillingPreview(null)
      setPreviewKey('')
      setRent(previousRent)
      setOptionPremium(currentOptionPremium)
      setAgreementSigners(agreementSigners)
      setDepositCredit(getSecurityDepositCreditAmount(newRental))
      setInitialOptionFee(DEFAULT_INITIAL_OPTION_FEE)
      setMonthlyOptionFee(DEFAULT_MONTHLY_OPTION_FEE)
      setPurchasePrice(null)
      setNotes('')
      setTerm(12)
      setShowOtherTerm(false)
      setCustomLeaseEndDate(null)
      const currentVariant = newRental.lease_variant === 'kcpoa' ? 'kcpoa' : 'standard'
      setLeaseVariant(currentVariant === 'kcpoa' && !isKCPOAEligible ? 'standard' : currentVariant)
    }
  }, [data, isKCPOAEligible, isOpen])

  const rentalAgreementHistories = excludeHoldoverAgreementHistories(
    rental?.rental_agreement_histories ?? []
  )
  const firstRentalAgreementHistory = [...rentalAgreementHistories].sort(
    (a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime()
  )[0]
  const latestRentalAgreementHistory =
    getLatestNonHoldoverAgreementHistory(rentalAgreementHistories)
  const previousRent = Number(latestRentalAgreementHistory?.rent ?? 0)
  const leaseEndDate = parseDate(latestRentalAgreementHistory?.ends_at)
  const renewalLeaseStartDate = addDays(leaseEndDate, 1)
  const addOns = (rental?.rental_add_ons ?? [])
    .filter(
      (addOn) =>
        parseDate(addOn.start_date) <= renewalLeaseStartDate &&
        (addOn.end_date === null || renewalLeaseStartDate <= parseDate(addOn.end_date))
    )
    .map((addOn) => ({
      price: Number(addOn.price),
      liability_type_name: addOn.liability_type.name
    }))
  const originalLeaseStartDate = parseDate(
    firstRentalAgreementHistory?.starts_at ?? rental?.occupancy_date
  )
  const usesCustomEndDate = showOtherTerm && !isKCPOA
  const hasValidRenewalTerm = usesCustomEndDate
    ? isValidCustomRenewalTerm(term)
    : isValidRenewalTerm(term, leaseVariant)
  const defaultRenewalLeaseEndDate =
    hasValidRenewalTerm && rental && latestRentalAgreementHistory
      ? calculateRenewalLeaseEndDate({
          currentLeaseEndDate: leaseEndDate,
          originalLeaseStartDate,
          renewalTerm: term,
          isMonthToMonth: !isKCPOA && term === 1
        })
      : null
  const renewalLeaseEndDate = usesCustomEndDate ? customLeaseEndDate : defaultRenewalLeaseEndDate
  const hasPrimaryRentalUser = Boolean(
    newAgreementSigners?.some((signer) => signer.role === agreementSigners.ROLES.PRIMARY_TENANT)
  )
  const canSubmit =
    hasValidRenewalTerm &&
    isValidRenewalRent(rent) &&
    Boolean(renewalLeaseEndDate) &&
    hasPrimaryRentalUser &&
    Boolean(agreementTemplateName) &&
    !isTemplateLoading &&
    !templateError &&
    (isKCPOA
      ? purchasePrice !== null &&
        purchasePrice > 0 &&
        initialOptionFee >= 0 &&
        monthlyOptionFee >= 0 &&
        depositCredit >= 0 &&
        depositCredit <= initialOptionFee
      : isValidRenewalOptionPremium(optionPremium))

  const renewalMonths =
    renewalLeaseEndDate &&
    isValid(renewalLeaseStartDate) &&
    isValid(renewalLeaseEndDate) &&
    renewalLeaseEndDate >= renewalLeaseStartDate &&
    differenceInMonths(renewalLeaseEndDate, renewalLeaseStartDate) <= 120
      ? eachMonthOfInterval({ start: renewalLeaseStartDate, end: renewalLeaseEndDate }).map(
          (month) => format(month, 'yyyy-MM')
        )
      : []
  const concessionValidationError =
    credits.length > 13
      ? 'Select no more than 13 credit months.'
      : credits.some((credit) => !renewalMonths.includes(credit.month))
      ? 'A selected credit month is outside the renewal period. Return to lease terms or remove that month.'
      : credits.some((credit) => !validCreditAmount(credit.amount))
      ? 'Enter a positive amount with no more than two decimal places for every selected month.'
      : credits.length && !concessionDescription.trim()
      ? 'Enter a description for the Rent concessions.'
      : ''
  const billingInput = {
    removedAddOnIds,
    ...(internetFeeMode ? { internetFeeMode } : {}),
    ...(credits.length
      ? {
          concessions: {
            description: concessionDescription.trim(),
            credits: [...credits]
              .sort((a, b) => a.month.localeCompare(b.month))
              .map((credit) => ({ month: credit.month, amount: Number(credit.amount) }))
          }
        }
      : {})
  }
  const previewPayload = {
    leaseVariant,
    extensionMonthsInDigits: term,
    useAlignedRenewalDates: !usesCustomEndDate,
    newLeaseRent: rent,
    optionPremium,
    notes,
    newLeaseStartDate: formatDate(renewalLeaseStartDate),
    newLeaseEndDate: formatDate(renewalLeaseEndDate),
    ...billingInput
  }
  const requestedPreviewKey = JSON.stringify(previewPayload)
  const canLoadPreview = !isKCPOA && canSubmit && !concessionValidationError
  const previewIsCurrent = Boolean(billingPreview && previewKey === requestedPreviewKey)

  useEffect(() => {
    if (!isOpen || !canLoadPreview) return
    let current = true
    setPreviewError('')
    setPreviewLoading(true)
    const timeout = window.setTimeout(() => {
      axios
        .post<CustomRenewalPreview>(
          `/admin/rentals/${rentalId}/renewal-preview`,
          JSON.parse(requestedPreviewKey)
        )
        .then(({ data: preview }) => {
          if (!current) return
          setBillingPreview(preview)
          if (!internetModeInitialized.current && preview.internet) {
            internetModeInitialized.current = true
            setInternetFeeMode(preview.internet.mode)
          }
          setPreviewKey(requestedPreviewKey)
        })
        .catch((error) => {
          if (!current) return
          setPreviewKey('')
          setPreviewError(
            error?.response?.data?.message ?? error?.message ?? 'Unable to load renewal preview.'
          )
        })
        .finally(() => {
          if (current) setPreviewLoading(false)
        })
    }, 300)
    return () => {
      current = false
      window.clearTimeout(timeout)
      setPreviewLoading(false)
    }
  }, [isOpen, canLoadPreview, requestedPreviewKey, rentalId, previewRetry])

  useEffect(() => {
    if (!isOpen || !rental) return

    let isCurrentRequest = true
    setAgreementTemplateName('')
    setTemplateError('')
    setIsTemplateLoading(true)

    axios
      .get(`/admin/rentals/${rentalId}/renewal-template`, {
        params: { leaseVariant }
      })
      .then(({ data: template }) => {
        if (isCurrentRequest) setAgreementTemplateName(template.name)
      })
      .catch((error) => {
        if (!isCurrentRequest) return
        setTemplateError(
          error?.response?.data?.message ??
            error?.message ??
            'Unable to find the active lease template.'
        )
      })
      .finally(() => {
        if (isCurrentRequest) setIsTemplateLoading(false)
      })

    return () => {
      isCurrentRequest = false
    }
  }, [isOpen, leaseVariant, rental, rentalId])

  if (!rental) {
    return (
      <Modal isOpen={isOpen} onClose={() => setOpen(false)} size='5xl'>
        <ModalOverlay />
        <ModalContent mx={4}>
          <ModalHeader>Issue custom renewal</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Center minH='240px'>
              <Spinner size='xl' />
            </Center>
          </ModalBody>
        </ModalContent>
      </Modal>
    )
  }

  const address = getAddressForRental(rental)
  const baseOptionPremium = rental.target_monthly_option_premium / 100
  const commonRenewalDetails = {
    fullAddress: address,
    landlordName: getLlc(rental)?.name,
    signers: newAgreementSigners ?? [],
    agreementTemplateName,
    originalLeaseDate: originalLeaseStartDate,
    extensionMonthsInDigits: term,
    newLeaseEndDate: renewalLeaseEndDate as Date,
    newLeaseStartDate: renewalLeaseStartDate,
    useAlignedRenewalDates: !usesCustomEndDate,
    newLeaseRent: rent,
    notes,
    addOns
  }
  const renewalDetails: RenewalDetails =
    leaseVariant === 'kcpoa'
      ? {
          ...commonRenewalDetails,
          leaseVariant,
          kcpoaTerms: {
            initialOptionFee,
            monthlyOptionFee,
            depositCredit,
            purchasePrice: purchasePrice!,
            stepUpIncreasePercentage: DEFAULT_KCPOA_STEP_UP_PERCENTAGE
          }
        }
      : {
          ...commonRenewalDetails,
          leaseVariant,
          optionPremium,
          ...billingInput,
          previewVersion: billingPreview?.previewVersion,
          internetFeeMode: internetFeeMode ?? billingPreview?.internet?.mode
        }

  const renewalContent = (
    <ModalContent maxH='calc(100vh - 2rem)' mx={4} my={4}>
      <ModalHeader pb={1}>
        {isKCPOA
          ? 'Issue custom renewal'
          : step === 'terms'
          ? '1. Lease terms'
          : '2. Fees and concessions'}
      </ModalHeader>
      <ModalCloseButton />
      <ModalBody ref={modalBodyRef} pb={0}>
        <Stack spacing={5}>
          {step === 'terms' && (
            <Alert status='info' borderRadius='md' alignItems='start'>
              <AlertIcon mt={1} />
              <Box>
                <AlertTitle>Use this tool to send a renewal directly.</AlertTitle>
                <AlertDescription>
                  This flow should only be used if you want to skip tenant renewal offer selection
                  and send a renewal lease with fixed terms. For most cases, use the{' '}
                  <Link as={RouterLink} color='teal.700' to='/properties/renewal-manager'>
                    Renewal Manager
                  </Link>{' '}
                  to send multiple renewal offers.
                </AlertDescription>
              </Box>
            </Alert>
          )}
          {step === 'terms' && (
            <>
              <RenewRentalModalDetails
                rent={previousRent}
                optionPremium={baseOptionPremium}
                address={address}
                leaseEndDate={leaseEndDate}
                addOns={addOns}
              />
              <RenewRentalModalForm
                rent={rent}
                setRent={setRent}
                previousRent={previousRent}
                optionPremium={optionPremium}
                setOptionPremium={setOptionPremium}
                leaseVariant={leaseVariant}
                setLeaseVariant={setLeaseVariant}
                isKCPOAEligible={isKCPOAEligible}
                initialOptionFee={initialOptionFee}
                setInitialOptionFee={setInitialOptionFee}
                monthlyOptionFee={monthlyOptionFee}
                setMonthlyOptionFee={setMonthlyOptionFee}
                depositCredit={depositCredit}
                setDepositCredit={setDepositCredit}
                purchasePrice={purchasePrice}
                setPurchasePrice={setPurchasePrice}
                term={term}
                setTerm={setTerm}
                notes={notes}
                setNotes={setNotes}
                showOtherTerm={showOtherTerm}
                setShowOtherTerm={setShowOtherTerm}
                startDate={renewalLeaseStartDate}
                endDate={renewalLeaseEndDate}
                setEndDate={(endDate) => {
                  setCustomLeaseEndDate(endDate)
                  setTerm(calculateRenewalTermFromDates(renewalLeaseStartDate, endDate))
                }}
                agreementSigners={newAgreementSigners ?? []}
              />
            </>
          )}
          {!isKCPOA && step === 'billing' && (
            <RenewalBillingFields
              preview={billingPreview}
              loading={previewLoading}
              error={previewError}
              months={renewalMonths}
              removedIds={removedAddOnIds}
              setRemovedIds={setRemovedAddOnIds}
              internetMode={internetFeeMode}
              setInternetMode={setInternetFeeMode}
              credits={credits}
              setCredits={setCredits}
              description={concessionDescription}
              setDescription={setConcessionDescription}
              total={concessionTotal}
              setTotal={setConcessionTotal}
              rent={rent}
              validationError={concessionValidationError}
              retry={() => setPreviewRetry((value) => value + 1)}
            />
          )}
          {templateError && (
            <Alert status='error' borderRadius='md'>
              <AlertIcon />
              <Box>
                <AlertTitle>Unable to load lease template</AlertTitle>
                <AlertDescription>{templateError}</AlertDescription>
              </Box>
            </Alert>
          )}
        </Stack>
      </ModalBody>

      <ModalFooter pt={5}>
        <Flex w='full' justify='flex-end' gap={3}>
          <Button variant='outline' onClick={() => setOpen(false)}>
            Cancel
          </Button>
          {step === 'billing' && (
            <Button variant='outline' onClick={() => setStep('terms')}>
              Back to lease terms
            </Button>
          )}
          <Button
            minW='180px'
            isDisabled={
              !canSubmit ||
              (!isKCPOA &&
                step === 'billing' &&
                (!previewIsCurrent ||
                  previewLoading ||
                  Boolean(concessionValidationError) ||
                  Boolean(previewError) ||
                  Boolean(billingPreview?.blockingErrors.length)))
            }
            colorScheme='teal'
            onClick={() => {
              if (!isKCPOA && step === 'terms') {
                setStep('billing')
                return
              }
              setOpen(false)
              setConfirmationIsOpen(true)
            }}
          >
            {!isKCPOA && step === 'terms' ? 'Continue to fees and concessions' : 'Review renewal'}
          </Button>
        </Flex>
      </ModalFooter>
    </ModalContent>
  )

  return (
    <>
      <Modal
        closeOnOverlayClick
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        scrollBehavior='inside'
        size='5xl'
      >
        <ModalOverlay bg='blackAlpha.500' />
        {renewalContent}
      </Modal>
      <RenewRentalConfirmation
        renewalDetails={renewalDetails}
        billingPreview={isKCPOA ? undefined : billingPreview ?? undefined}
        isOpen={confirmationIsOpen}
        setOpen={setConfirmationIsOpen}
        goBack={(open) => {
          preserveFormOnNextOpen.current = true
          setOpen(open)
        }}
        onSuccess={onSuccess}
      />
    </>
  )
}

export default RenewRentalModal
